import { setToken, clearToken, getToken } from './tokenStore';

const isDevelopment = process.env.NODE_ENV === 'development';
const AUTH_BASE = isDevelopment ? 'http://localhost:8085' : '/api/auth';
const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || (isDevelopment ? 'http://localhost:9082' : '/keycloak');
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'aspect';

export interface TokenPayload {
  sub: string;
  preferred_username: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  realm_access?: { roles: string[] };
  exp: number;
}

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  roles: string[];
  displayName?: string;
}

interface BackendUser {
  sub?: string;
  id?: string;
  username?: string;
  email?: string;
  roles?: string[];
  name?: string;
  displayName?: string;
  display_name?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user?: BackendUser;
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(AUTH_BASE + path, {
    credentials: 'include', // send HttpOnly refresh cookie
    headers: { 'Content-Type': 'application/json', ...init.headers },
    ...init,
  });
}

export async function authRequest(path: string, options: RequestInit = {}, _retry = false): Promise<Response> {
  const token = getToken();
  const res = await fetch(AUTH_BASE + path, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> || {}),
    },
  });
  if (res.status === 401 && !_retry) {
    const user = await refresh().catch(() => null);
    if (user) return authRequest(path, options, true);
    clearToken();
  }
  return res;
}

export function loginWithGoogle(): void {
  const params = new URLSearchParams({
    client_id: 'arch-editor-frontend',
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: 'code',
    scope: 'openid email profile',
    kc_idp_hint: 'google',
  });
  window.location.href = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth?${params}`;
}

export async function exchangeCode(code: string): Promise<AuthUser> {
  const res = await authFetch('/auth/callback', {
    method: 'POST',
    body: JSON.stringify({
      code,
      redirect_uri: `${window.location.origin}/auth/callback`,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err.message ?? err.error ?? `OAuth callback failed (${res.status})`);
  }
  const data: LoginResponse = await res.json();
  setToken(data.access_token);
  return decodeUser(data.access_token, data.user);
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const res = await authFetch('/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err.message ?? err.error ?? `Login failed (${res.status})`);
  }
  const data: LoginResponse = await res.json();
  setToken(data.access_token);
  return decodeUser(data.access_token, data.user);
}

export async function register(
  username: string,
  email: string,
  password: string,
  displayName?: string,
): Promise<AuthUser | null> {
  const res = await authFetch('/register', {
    method: 'POST',
    body: JSON.stringify({ username, email, password, display_name: displayName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err.message ?? err.error ?? `Registration failed (${res.status})`);
  }
  const data = await res.json() as Partial<LoginResponse> & { login_required?: boolean; email_verification_required?: boolean };
  if (data.login_required || data.email_verification_required || !data.access_token) {
    // User created but auto-login blocked by Keycloak required actions/email verification.
    // Caller should redirect to login.
    return null;
  }
  setToken(data.access_token);
  return decodeUser(data.access_token, data.user);
}

export async function refresh(): Promise<AuthUser | null> {
  const res = await authFetch('/refresh', { method: 'POST' });
  if (!res.ok) {
    clearToken();
    return null;
  }
  const data: LoginResponse = await res.json();
  setToken(data.access_token);
  return decodeUser(data.access_token, data.user);
}

export async function logout(): Promise<void> {
  try {
    await authFetch('/logout', { method: 'POST' });
  } finally {
    clearToken();
  }
}

export function getMe(): AuthUser | null {
  const token = getToken();
  if (!token) return null;
  try {
    return decodeUser(token);
  } catch {
    return null;
  }
}

function decodeUser(token: string, backendUser?: BackendUser): AuthUser {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT');
  const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as TokenPayload;
  const displayName = backendUser?.displayName
    ?? backendUser?.display_name
    ?? backendUser?.name
    ?? payload.name
    ?? [payload.given_name, payload.family_name].filter(Boolean).join(' ')
    ?? undefined;

  return {
    id: backendUser?.sub ?? backendUser?.id ?? payload.sub,
    username: backendUser?.username ?? payload.preferred_username,
    email: backendUser?.email ?? payload.email,
    roles: backendUser?.roles ?? payload.realm_access?.roles ?? [],
    ...(displayName ? { displayName } : {}),
  };
}
